<template>
  <UiDialog v-model:open="isOpen" :title="title || 'Add Words'">
    <div class="input-drawer-content flex flex-col gap-4">
      <div
        v-if="!isOnline"
        data-testid="offline-llm-warning"
        class="p-3 rounded-xl border border-rose-500/40 bg-rose-500/10 text-rose-600 dark:text-rose-400 text-xs font-medium flex items-center gap-2"
      >
        <i class="fa-solid fa-triangle-exclamation text-sm shrink-0"></i>
        <span>
          Offline mode: Adding new words and file/photo extraction require an internet connection for AI processing.
        </span>
      </div>

      <p class="text-sm text-gray-600 dark:text-gray-300">
        {{ promptText || 'Type or paste words below, or upload a text file / subtitle / document / photo:' }}
      </p>

      <!-- Text Input Area -->
      <div class="textarea-wrapper">
        <textarea
          v-model="inputText"
          rows="4"
          placeholder="e.g. apple, banana - custom list..."
          class="w-full p-3 rounded-lg border text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 bg-transparent"
          :disabled="isSubmitting"
        ></textarea>
      </div>

      <!-- File Attachment Zone -->
      <div class="file-upload-zone border-2 border-dashed rounded-xl p-4 flex flex-col items-center justify-center text-center cursor-pointer transition hover:bg-gray-50/50 dark:hover:bg-gray-800/50">
        <input
          type="file"
          ref="fileInputRef"
          class="hidden"
          accept=".txt,.srt,.docx,.pdf,image/*"
          @change="onFileSelected"
        />
        <div @click="triggerFileInput" class="w-full flex flex-col items-center">
          <i class="fa-solid fa-cloud-arrow-up text-2xl text-blue-500 mb-2"></i>
          <p class="text-xs font-semibold text-gray-700 dark:text-gray-200">
            {{ selectedFile ? selectedFile.name : 'Upload .txt, .srt, .docx, or photo' }}
          </p>
          <p class="text-[11px] text-gray-400 mt-0.5">
            {{ selectedFile ? `${(selectedFile.size / 1024).toFixed(1)} KB` : 'Click to browse files' }}
          </p>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex items-center justify-end gap-2">
        <UiButton variant="ghost" :disabled="isSubmitting" @click="isOpen = false">
          Cancel
        </UiButton>
        <UiButton
          variant="primary"
          :disabled="isSubmitting || (!inputText.trim() && !selectedFile)"
          @click="handleSubmit"
        >
          <UiSpinner v-if="isSubmitting" class="mr-1.5" />
          <span>Submit</span>
        </UiButton>
      </div>
    </template>
  </UiDialog>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { UiDialog, UiButton, UiSpinner } from '../ui';
import { useSyncStatus } from '../composables/useSyncStatus';

const isOpen = defineModel<boolean>('open', { default: false });

defineProps<{
  title?: string;
  promptText?: string;
  isSubmitting?: boolean;
}>();

const emit = defineEmits<{
  'submit-text': [text: string];
  'submit-file': [file: File];
}>();

const { isOnline } = useSyncStatus();
const inputText = ref('');
const selectedFile = ref<File | null>(null);
const fileInputRef = ref<HTMLInputElement | null>(null);

const triggerFileInput = () => {
  fileInputRef.value?.click();
};

const onFileSelected = (event: Event) => {
  const target = event.target as HTMLInputElement;
  if (target.files && target.files.length > 0) {
    selectedFile.value = target.files[0];
  }
};

const handleSubmit = () => {
  if (selectedFile.value) {
    emit('submit-file', selectedFile.value);
    if (isOnline.value) {
      selectedFile.value = null;
      inputText.value = '';
    }
    return;
  }
  if (inputText.value.trim()) {
    emit('submit-text', inputText.value.trim());
    if (isOnline.value) {
      inputText.value = '';
      selectedFile.value = null;
    }
  }
};
</script>

<style scoped>
.textarea-wrapper textarea {
  border-color: var(--ui-border-control);
  color: var(--ui-text);
  background: var(--ui-surface);
}
.file-upload-zone {
  border-color: var(--ui-border);
}
</style>
